import { useEffect, useState } from 'react';
import { api } from '../api/client';
import type { AdminAnalytics, AdminUser, Tier } from '../api/types';
import { useUser } from '../state/UserContext';

type Tab = 'users' | 'analytics';

function formatPercent(fraction: number): string {
  return `${Math.round(fraction * 100)}%`;
}

export function Admin() {
  const { me } = useUser();
  const [tab, setTab] = useState<Tab>('users');

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [query, setQuery] = useState('');
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [updatingSub, setUpdatingSub] = useState<string | null>(null);

  const [analytics, setAnalytics] = useState<AdminAnalytics | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [analyticsError, setAnalyticsError] = useState<string | null>(null);

  const loadUsers = (q?: string) => {
    setUsersLoading(true);
    setUsersError(null);
    api
      .getAdminUsers(q)
      .then((res) => setUsers(res.users))
      .catch((e) => setUsersError(e instanceof Error ? e.message : 'Could not load users.'))
      .finally(() => setUsersLoading(false));
  };

  const loadAnalytics = () => {
    setAnalyticsLoading(true);
    setAnalyticsError(null);
    api
      .getAdminAnalytics()
      .then(setAnalytics)
      .catch((e) => setAnalyticsError(e instanceof Error ? e.message : 'Could not load analytics.'))
      .finally(() => setAnalyticsLoading(false));
  };

  useEffect(loadUsers, []);
  useEffect(loadAnalytics, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    loadUsers(query.trim() || undefined);
  };

  const handleToggleTier = async (user: AdminUser) => {
    const nextTier: Tier = user.tier === 'premium' ? 'free' : 'premium';
    setUpdatingSub(user.sub);
    setUsersError(null);
    try {
      const updated = await api.updateAdminUser(user.sub, { tier: nextTier });
      setUsers((prev) => prev.map((u) => (u.sub === user.sub ? updated : u)));
    } catch (e) {
      setUsersError(e instanceof Error ? e.message : 'Could not update this user.');
    } finally {
      setUpdatingSub(null);
    }
  };

  if (!me?.isAdmin) {
    return (
      <div>
        <h1 className="page-title">Admin</h1>
        <p className="page-subtitle">You don&rsquo;t have access to this page.</p>
      </div>
    );
  }

  return (
    <div>
      <h1 className="page-title">Admin</h1>
      <p className="page-subtitle">User management and usage analytics.</p>

      <div className="settings-tabs">
        <button type="button" className={`settings-tab${tab === 'users' ? ' active' : ''}`} onClick={() => setTab('users')}>
          <span aria-hidden="true">👥</span> Users
        </button>
        <button type="button" className={`settings-tab${tab === 'analytics' ? ' active' : ''}`} onClick={() => setTab('analytics')}>
          <span aria-hidden="true">📊</span> Analytics
        </button>
      </div>

      {tab === 'users' && (
        <div className="settings-panel">
          <form onSubmit={handleSearch} className="admin-search-row">
            <input
              type="text"
              placeholder="Search by email or name"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="admin-search-input"
            />
            <button type="submit" className="btn btn-secondary">Search</button>
          </form>

          {usersError && <p className="error-text">{usersError}</p>}

          {usersLoading ? (
            <div className="center-page" style={{ minHeight: '20vh' }}>
              <div className="spinner" />
            </div>
          ) : users.length === 0 ? (
            <div className="empty-state">No users found.</div>
          ) : (
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Name</th>
                    <th>Tier</th>
                    <th>Streak</th>
                    <th>Longest</th>
                    <th>Practice days</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.sub}>
                      <td>{u.email || '—'}</td>
                      <td>{u.displayName || '—'}</td>
                      <td>
                        <span className={`admin-tier-badge${u.tier === 'premium' ? ' premium' : ''}`}>{u.tier}</span>
                      </td>
                      <td>{u.currentStreak}</td>
                      <td>{u.longestStreak}</td>
                      <td>{u.totalPracticeDays}</td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-secondary btn-small"
                          onClick={() => handleToggleTier(u)}
                          disabled={updatingSub === u.sub}
                        >
                          {updatingSub === u.sub ? '…' : u.tier === 'premium' ? 'Revoke Premium' : 'Grant Premium'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'analytics' && (
        <div className="settings-panel">
          {analyticsError && <p className="error-text">{analyticsError}</p>}

          {analyticsLoading ? (
            <div className="center-page" style={{ minHeight: '20vh' }}>
              <div className="spinner" />
            </div>
          ) : analytics ? (
            <>
              <div className="admin-stat-grid">
                <div className="admin-stat-card">
                  <div className="admin-stat-value">{analytics.totalUsers}</div>
                  <div className="admin-stat-label">Total users</div>
                </div>
                <div className="admin-stat-card">
                  <div className="admin-stat-value">{analytics.premiumUsers}</div>
                  <div className="admin-stat-label">Premium ({formatPercent(analytics.premiumRate)})</div>
                </div>
                <div className="admin-stat-card">
                  <div className="admin-stat-value">{analytics.usersWithActiveStreak}</div>
                  <div className="admin-stat-label">Active streaks</div>
                </div>
                <div className="admin-stat-card">
                  <div className="admin-stat-value">{analytics.avgCurrentStreak}</div>
                  <div className="admin-stat-label">Avg. current streak</div>
                </div>
                <div className="admin-stat-card">
                  <div className="admin-stat-value">{analytics.totalDistinctPracticeCompletions}</div>
                  <div className="admin-stat-label">Distinct completions</div>
                </div>
                <div className="admin-stat-card">
                  <div className="admin-stat-value">{formatPercent(analytics.leaderboardOptInRate)}</div>
                  <div className="admin-stat-label">Leaderboard opt-in</div>
                </div>
              </div>

              <h2 className="admin-section-heading">By tradition</h2>
              {Object.keys(analytics.usersByTradition).length === 0 ? (
                <div className="empty-state">No traditions selected yet.</div>
              ) : (
                <div className="admin-tradition-list">
                  {Object.entries(analytics.usersByTradition).map(([tradition, count]) => (
                    <div key={tradition} className="admin-tradition-row">
                      <span>{tradition}</span>
                      <span className="admin-tradition-count">{count}</span>
                    </div>
                  ))}
                </div>
              )}

              {analytics.usersWithSignupDate < analytics.totalUsers && (
                <p className="practice-item-meta" style={{ marginTop: 16 }}>
                  Signup-date tracking started with this dashboard — {analytics.usersWithSignupDate} of{' '}
                  {analytics.totalUsers} accounts have a real signup date on record.
                </p>
              )}
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}
